import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabaseGuard';

type MessagesChannelStatus =
  | 'SUBSCRIBED'
  | 'TIMED_OUT'
  | 'CLOSED'
  | 'CHANNEL_ERROR';

export type MessagesChannelStatusHandler = (
  status: MessagesChannelStatus,
  error?: Error
) => void;

export type MessagesChannelFactory = (
  userId: string,
  onStatus: MessagesChannelStatusHandler
) => RealtimeChannel;

let messagesChannel: RealtimeChannel | null = null;
let messagesChannelFactory: MessagesChannelFactory | null = null;
let messagesChannelUserId: string | null = null;
let messagesChannelNeedsRecovery = false;
let recoveryInProgress = false;
let activeGeneration = 0;

type SocketSnapshot = 'connecting' | 'open' | 'closing' | 'closed' | null;

const handleStatus: MessagesChannelStatusHandler = (status, error) => {
  if (status === 'SUBSCRIBED') {
    messagesChannelNeedsRecovery = false;
    return;
  }

  if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
    messagesChannelNeedsRecovery = true;
    if (status === 'CLOSED' || !navigator.onLine || error == null) {
      return;
    }
    console.warn('[network-recovery] Messages channel error', {
      status,
      error,
      online: navigator.onLine,
    });
  }
};

function bindStatus(generation: number): MessagesChannelStatusHandler {
  return (status, error) => {
    if (generation !== activeGeneration) return;
    handleStatus(status, error);
  };
}

function invalidateChannelGeneration(): void {
  activeGeneration += 1;
}

function readSocketState(): SocketSnapshot {
  try {
    const state = supabase.realtime?.connectionState?.();
    if (
      state === 'connecting' ||
      state === 'open' ||
      state === 'closing' ||
      state === 'closed'
    ) {
      return state;
    }
  } catch {
    // A missing socket snapshot must not force a recreate.
  }
  return null;
}

/**
 * Healthy joined/open and in-flight joining channels are left alone.
 * Stale states are recreated once on the next recovery call.
 */
function shouldRecreateMessagesChannel(channel: RealtimeChannel | null): boolean {
  const channelState = channel?.state;
  const socket = readSocketState();

  if (channelState === 'joined' && socket === 'open') return false;
  if (channelState === 'joining' && (socket === 'connecting' || socket === 'open')) {
    return false;
  }

  if (channelState === 'errored' || channelState === 'closed') return true;
  if (channelState === 'joined' && socket === 'closed') return true;

  return messagesChannelNeedsRecovery;
}

export function createMessagesChannel(
  factory: MessagesChannelFactory,
  userId: string
): RealtimeChannel {
  return factory(userId, bindStatus(activeGeneration));
}

export function subscribeToMessagesChannel(
  factory: MessagesChannelFactory,
  userId: string
): RealtimeChannel {
  if (messagesChannel && messagesChannelUserId !== userId) {
    const staleChannel = messagesChannel;
    messagesChannel = null;
    messagesChannelNeedsRecovery = false;
    invalidateChannelGeneration();
    void supabase.removeChannel(staleChannel).catch((error) => {
      console.warn('[network-recovery] Failed removing previous user messages channel', error);
    });
  }

  messagesChannelFactory = factory;
  messagesChannelUserId = userId;

  if (!messagesChannel) {
    invalidateChannelGeneration();
    messagesChannel = createMessagesChannel(factory, userId);
  }

  return messagesChannel;
}

export function unsubscribeFromMessagesChannel(
  factory: MessagesChannelFactory
): void {
  if (messagesChannelFactory !== factory) return;

  messagesChannelFactory = null;
  messagesChannelUserId = null;
  messagesChannelNeedsRecovery = false;
  invalidateChannelGeneration();

  const staleChannel = messagesChannel;
  messagesChannel = null;

  if (staleChannel) {
    void supabase.removeChannel(staleChannel).catch((error) => {
      console.warn('[network-recovery] Failed removing messages channel', error);
    });
  }
}

/**
 * Recreate the active messages channel when it is stale, or when a wake/online
 * caller passes force before CHANNEL_ERROR can set messagesChannelNeedsRecovery.
 * Concurrency locks, offline, and per-user ownership still apply. `force` does
 * not create a second channel while recoveryInProgress is set.
 */
export async function recoverMessagesChannel(force: boolean = false): Promise<void> {
  if (
    recoveryInProgress ||
    !messagesChannelFactory ||
    !messagesChannelUserId ||
    !navigator.onLine
  ) {
    return;
  }

  if (!force && !shouldRecreateMessagesChannel(messagesChannel)) {
    return;
  }

  recoveryInProgress = true;
  const factory = messagesChannelFactory;
  const userId = messagesChannelUserId;
  const staleChannel = messagesChannel;

  // Ignore CLOSED from the channel we are intentionally removing.
  invalidateChannelGeneration();
  messagesChannelNeedsRecovery = false;

  // Null first so no caller can reuse the stale channel while removal awaits.
  messagesChannel = null;

  try {
    if (staleChannel) {
      try {
        await supabase.removeChannel(staleChannel);
      } catch (error) {
        console.warn('[network-recovery] Failed removing stale messages channel', error);
      }
    }

    if (
      !navigator.onLine ||
      messagesChannelFactory !== factory ||
      messagesChannelUserId !== userId
    ) {
      messagesChannelNeedsRecovery = true;
      return;
    }

    messagesChannel = createMessagesChannel(factory, userId);
  } catch (error) {
    messagesChannelNeedsRecovery = true;
    console.warn('[network-recovery] Messages channel recovery failed', error);
  } finally {
    recoveryInProgress = false;
  }
}
